import React from 'react';

import useStoreTranslation from 'hooks/useStoreTranslation';
import { Price } from 'models/price';
import { getFormattedPrice } from 'utils/currency';

import { Card, Division, PrivateFareIconContainer, PrivateFareMessage } from './styles';
import { ThemeType } from 'theme/theme';
import { useTheme } from 'styled-components';
import { ReactComponent as TicketIcon } from 'assets/images/private-fare.svg';
import { FlightValidationResponse } from 'models/flightValidation';

interface Props {
  price: Price;
  travelers: number;
  alternativePrice?: Price;
  details: FlightValidationResponse;
  isBySector?: boolean;
}

const FlightPrice = ({ price, alternativePrice, travelers, details, isBySector }: Props) => {
  const theme = useTheme() as ThemeType;
  const { t } = useStoreTranslation();
  const formattedPrice = (value: number | undefined, currencyCode: string) => {
    const { formatted } = getFormattedPrice({ value, currency: currencyCode, t });
    return formatted;
  };

  const discountValue =
    price.discountAmount && price.discountAmount > 0 ? price.discountAmount * -1 : price.discountAmount;

  return (
    <>
      {!isBySector && (
        <Card>
          <Card.Section>
            <Card.Title>{t('flightDetails.price.value')}</Card.Title>
            <Card.Label>{`${travelers} ${t('flightDetails.price.adult')}`}</Card.Label>
          </Card.Section>
          <Division />
          <Card.Section>
            <Card.Row>
              <Card.Label>{t('flightDetails.price.fare')}</Card.Label>
              <Card.Value>{formattedPrice(price.amount, price.currencyCode)}</Card.Value>
            </Card.Row>
            <Card.Row>
              <Card.Label>{t('flightDetails.price.taxes')}</Card.Label>
              <Card.Value>
                {formattedPrice(
                  (price.taxes || price.taxesValue || 0) + (price.fees || 0) + (price.surcharges || 0),
                  price.currencyCode,
                )}
              </Card.Value>
            </Card.Row>
            <Card.Row>
              <Card.Label>{t('flightDetails.price.discount')}</Card.Label>
              <Card.Value>{formattedPrice(discountValue, price.currencyCode)}</Card.Value>
            </Card.Row>

            <Card.Row>
              <Card.TotalLabel>{t('flightDetails.price.total')}</Card.TotalLabel>
              <Card.Total>{formattedPrice(price.total, price.currencyCode)}</Card.Total>
            </Card.Row>
            {alternativePrice && (
              <>
                <Card.Row>
                  <Card.TotalLabel></Card.TotalLabel>

                  <Card.Total>
                    {t('alternativePrice.or')} {formattedPrice(alternativePrice.total, alternativePrice.currencyCode)}
                  </Card.Total>
                </Card.Row>
              </>
            )}
            {details.tripOption.hasPrivateFare && (
              <Card.Row>
                <Card.TotalLabel></Card.TotalLabel>
                <Card.Total>
                  <PrivateFareMessage>
                    <PrivateFareIconContainer>
                      <TicketIcon stroke={theme.primaryBackgroundColor} />
                    </PrivateFareIconContainer>
                    {t('flightDetails.privateFare')}
                  </PrivateFareMessage>
                </Card.Total>
              </Card.Row>
            )}
          </Card.Section>
        </Card>
      )}
      {isBySector && (
        <Card>
          <Card.Section>
            <Card.Title>{t('flightDetails.price.value')}</Card.Title>
            <Card.Label>{`${travelers} ${t('flightDetails.price.adult')}`}</Card.Label>
          </Card.Section>
          <Division />
          <Card.Section>
          <Card.Row>
              <Card.Label>{t('flightDetails.price.fare')}</Card.Label>
              <Card.Value>{formattedPrice(price.amount, price.currencyCode)}</Card.Value>
            </Card.Row>
            <Card.Row>
              <Card.Label>{t('flightDetails.price.taxes')}</Card.Label>
              <Card.Value>
                {formattedPrice(
                  (price.taxes || price.taxesValue || 0) + (price.fees || 0) + (price.surcharges || 0),
                  price.currencyCode,
                )}
              </Card.Value>
            </Card.Row>
            <Card.Row>
              <Card.Label>{t('flightDetails.price.discount')}</Card.Label>
              <Card.Value>{formattedPrice(discountValue, price.currencyCode)}</Card.Value>
            </Card.Row>
            <Card.Row>
              <Card.TotalLabel>{t('flightDetails.price.total')}</Card.TotalLabel>
              <Card.Total>{formattedPrice(price.total, price.currencyCode)}</Card.Total>
            </Card.Row>
            {alternativePrice && (
              <>
                <Card.Row>
                  <Card.TotalLabel></Card.TotalLabel>

                  <Card.Total>
                    {t('alternativePrice.or')} {formattedPrice(alternativePrice.total, alternativePrice.currencyCode)}
                  </Card.Total>
                </Card.Row>
              </>
            )}
          </Card.Section>
        </Card>
      )}
    </>
  );
};

export default FlightPrice;
